import type { BrandScoreDailyRow } from '../types';

/** Row shape from brands_models_score_daily API (fields used for top model by visibility). */
export interface TopModelVisibilityRow {
  model_id?: number | string | null;
  avg_visibility_score: number;
}

/**
 * Aggregates rows by model_id (average visibility) and returns the model_id with the highest average.
 * Uses normalized number keys so that model_id from API (number or string) is grouped correctly.
 * Returns null if no rows or no valid model_id in any row.
 */
export function getTopModelByVisibility(
  rows: TopModelVisibilityRow[] | BrandScoreDailyRow[]
): number | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const byModel = new Map<number, { sumVisibility: number; count: number }>();

  for (const row of rows) {
    const raw = row.model_id;
    if (raw === undefined || raw === null) continue;

    const modelId = Number(raw);
    if (!Number.isFinite(modelId)) continue;

    const cur = byModel.get(modelId) ?? { sumVisibility: 0, count: 0 };
    cur.sumVisibility += Number(row.avg_visibility_score);
    cur.count += 1;
    byModel.set(modelId, cur);
  }

  if (byModel.size === 0) return null;

  let topModelId: number | null = null;
  let maxAvg = -Infinity;

  for (const [modelId, agg] of byModel) {
    const avg = agg.sumVisibility / agg.count;
    if (avg > maxAvg) {
      maxAvg = avg;
      topModelId = modelId;
    }
  }

  return topModelId;
}
